import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useMemo, useState } from "react";

import type { ConstraintListItemDto } from "@/api/types";
import { AnnotationInfoIcon } from "@/components/Icons/AnnotationInfoIcon";
import { Button } from "@/design-system/Button";
import { Drawer } from "@/design-system/Drawer";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { MultiSelect } from "@/design-system/MultiSelect";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { flattenAudience, getIconByAudienceType, mapAudienceByType, orderAudienceTypes } from "@/helpers/audience";
import { useBool } from "@/hooks/useBool";
import { useGeneralQueries } from "@/queries/general/queryOptions";
import { useTranslation } from "@/translations";

import { AudiencePreview } from "./AudiencePreview";

type AudienceType = ConstraintListItemDto["type"];

type AudienceSelectorProps = {
  defaultAudience: ConstraintListItemDto[];
  onSave: (audiences: ConstraintListItemDto[]) => void;
  isEditMode: boolean;
  editWarningMessage?: string;
};

export function AudienceSelector({
  defaultAudience,
  isEditMode,
  editWarningMessage,
  onSave,
}: AudienceSelectorProps): React.ReactNode {
  const [isDrawerOpened, drawerHandler] = useBool();

  const defaultAudienceGroupByType = mapAudienceByType(defaultAudience);
  const [currAudience, setCurrAudience] =
    useState<Record<AudienceType, ConstraintListItemDto[]>>(defaultAudienceGroupByType);

  const { t } = useTranslation();
  const generalQueries = useGeneralQueries();
  const {
    data: constraints,
    isPending: isPendingConstraints,
    isError: isErrorConstraints,
  } = useQuery(generalQueries.constraints());

  const onChangeAudienceItems = (type: AudienceType, items: ConstraintListItemDto[]) => {
    setCurrAudience((prevValue) => ({ ...prevValue, [type]: items }));
  };

  // Reset to existing audience when closing the drawer witout saving
  const onClickClose = () => {
    setCurrAudience(defaultAudienceGroupByType);
  };

  const onClickSave = () => {
    onSave(flattenAudience(currAudience));
  };

  const availableAudience = useMemo(() => mapAudienceByType(constraints?.items || []), [constraints?.items]);
  const availableAudienceTypes = Object.keys(availableAudience) as AudienceType[];
  const configTexts = {
    apartmentType: {
      title: t("model.constraint.address"),
      description: t("component.audience-selector.general-description", {
        audienceTarget: t("common.entity.asset").toLowerCase(),
        audienceEntity: t("model.constraint.type.apartment-type").toLowerCase(),
      }),
      placeholder: t("component.audience-selector.general-placeholder", {
        audienceEntity: t("model.constraint.type.apartment-type").toLowerCase(),
      }),
    },
    floor: {
      title: t("model.constraint.floor"),
      description: t("component.audience-selector.general-description", {
        audienceTarget: t("common.entity.asset").toLowerCase(),
        audienceEntity: t("model.constraint.type.floor").toLowerCase(),
      }),
      placeholder: t("component.audience-selector.general-placeholder", {
        audienceEntity: t("model.constraint.type.floor").toLowerCase(),
      }),
    },
    building: {
      title: t("model.constraint.building"),
      description: t("component.audience-selector.general-description", {
        audienceTarget: t("common.entity.asset").toLowerCase(),
        audienceEntity: t("model.constraint.type.building").toLowerCase(),
      }),
      placeholder: t("component.audience-selector.general-placeholder", {
        audienceEntity: t("model.constraint.type.building").toLowerCase(),
      }),
    },
  };

  // Hide audience selector if there are no constraints available (aka no address)
  if (!isPendingConstraints && !isErrorConstraints && constraints.items.length === 0) {
    return null;
  }

  return (
    <>
      <AudiencePreview audience={defaultAudience} onClick={drawerHandler.setTrue} truncate />
      <Drawer.Root
        title={t("component.audience-selector.title")}
        closeLabel={t("common.action.close")}
        isOpened={isDrawerOpened}
        onOpenChange={drawerHandler.set}
      >
        {isPendingConstraints && <FullSizeLoader />}
        {!isPendingConstraints && !isErrorConstraints && (
          <>
            <Drawer.Body>
              <Stack gap="md">
                {isEditMode && editWarningMessage && (
                  <Notice variant="warning" icon={<AnnotationInfoIcon />} message={editWarningMessage} />
                )}
                <Stack as="section" data-testid="audience-root" gap="md">
                  {/* Order audience sections by a specific ordering */}
                  {orderAudienceTypes(availableAudienceTypes).map((audienceType) => {
                    const icon = getIconByAudienceType(audienceType);

                    return (
                      <Stack as="section" key={audienceType} data-testid="audience-item" gap="xs">
                        <header className="flex w-full items-center justify-start gap-space-xs">
                          {icon}
                          <h2 className="text-heading-4">{configTexts[audienceType].title}</h2>
                        </header>
                        <p className="text-text-secondary">{configTexts[audienceType].description}</p>
                        <MultiSelect<ConstraintListItemDto>
                          selected={currAudience[audienceType]}
                          placeholder={configTexts[audienceType].placeholder}
                          items={availableAudience[audienceType]}
                          keySelector={({ id }) => id}
                          renderOption={({ key }) => key}
                          onChange={(items) => onChangeAudienceItems(audienceType, items)}
                        />
                      </Stack>
                    );
                  })}
                </Stack>
              </Stack>
            </Drawer.Body>
            <Drawer.Actions>
              <Drawer.Close>
                <Button variant="subtle" tone="primary" onClick={onClickClose}>
                  {t("common.action.cancel")}
                </Button>
              </Drawer.Close>
              <Drawer.Close>
                <Button variant="filled" onClick={onClickSave}>
                  {t("common.action.set")}
                </Button>
              </Drawer.Close>
            </Drawer.Actions>
          </>
        )}
      </Drawer.Root>
    </>
  );
}
